<template>
  <div class="message-inbox">
    <div v-if="!loggedIn">
      <h3>Geschlossener Bereich</h3>
      <Button label="Login erforderlich" @click="routeToLogin()" />
    </div>
    <div v-else>
      <div class="inbox-toolbar">
        <SelectButton
          v-model="direction"
          :options="availableDirections"
          optionLabel="label"
          optionValue="value"
          :unselectable="false"
        />
        <Button
          v-if="direction != 'leagues'"
          label="Neue Nachricht"
          icon="pi pi-pencil"
          @click="displayComposeDialog = true"
        />
        <Button
          class="p-button-outlined p-button-sm"
          label="Aktualisieren"
          icon="pi pi-refresh"
          @click="loadConversations()"
        />
        <span v-if="direction == 'leagues'" class="inbox-count">
          {{ leagueConversations.length }} Anfragen in deinen Ligen
        </span>
        <span v-else class="inbox-count">
          {{ visibleConversations.length }} Nachrichten<span v-if="unreadCount > 0">
            &middot; {{ unreadCount }} ungelesen</span
          >
        </span>
      </div>

      <DataTable
        v-if="direction == 'leagues'"
        class="p-datatable-sm"
        :value="leagueConversations"
        dataKey="id"
        stripedRows
        responsiveLayout="stack"
        breakpoint="650px"
        sortField="updateTimestamp"
        :sortOrder="-1"
      >
        <template #empty> In deinen Ligen liegen keine Anfragen vor. </template>
        <Column header="Letzte Änderung" field="updateTimestamp" sortable>
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.updateTimestamp) }}
          </template>
        </Column>
        <Column header="Betreff" field="subject" sortable />
        <Column header="Ersteller" field="requestParticipant" sortable />
        <Column header="Ersteller gelesen am">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.requestReadTimestamp) }}
          </template>
        </Column>
        <Column header="Angefragter" field="answerParticipant" sortable />
        <Column header="Angefragter gelesen am">
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.answerReadTimestamp) }}
          </template>
        </Column>
        <Column header="Antwort">
          <template #body="slotProps">
            <div v-html="sanitize(slotProps.data.resultingAnswer)" />
          </template>
        </Column>
      </DataTable>
      <DataTable
        v-else
        class="p-datatable-sm"
        :value="visibleConversations"
        :rowClass="conversationRowClass"
        dataKey="id"
        stripedRows
        responsiveLayout="stack"
        breakpoint="650px"
        sortField="updateTimestamp"
        :sortOrder="-1"
        @row-click="openConversation($event.data)"
      >
        <template #empty>
          {{
            direction == "incoming"
              ? "Es liegen keine Nachrichten für dich vor."
              : "Du hast noch nichts gesendet oder beantwortet."
          }}
        </template>
        <Column header="Letzte Änderung" field="updateTimestamp" sortable>
          <template #body="slotProps">
            {{ formatDateTime(slotProps.data.updateTimestamp) }}
          </template>
        </Column>
        <Column :header="direction == 'incoming' ? 'Von' : 'An'" field="otherParticipant" sortable>
          <template #body="slotProps">
            {{ slotProps.data.otherParticipant }}
            <span
              v-if="broadcastSize(slotProps.data) > 1"
              class="broadcast-hint"
              v-tooltip.top="'Teil eines Rundschreibens an mehrere Empfänger'"
            >
              (+{{ broadcastSize(slotProps.data) - 1 }})
            </span>
          </template>
        </Column>
        <Column
          v-if="direction == 'incoming'"
          header="Empfangen als"
          field="ownParticipant"
          sortable
        />
        <Column header="Betreff" field="subject" sortable />
        <Column
          :header="direction == 'outgoing' ? 'Vom Empfänger gelesen' : 'Gelesen'"
        >
          <template #body="slotProps">
            {{ readInfo(slotProps.data) }}
          </template>
        </Column>
        <Column header="Antwort" field="lastMessageOwn" sortable>
          <template #body="slotProps">
            <span class="reply-state" :class="replyStateClass(slotProps.data)">
              {{ replyState(slotProps.data) }}
            </span>
          </template>
        </Column>
        <Column header="Aktionen" style="width: 9rem">
          <template #body="slotProps">
            <Button
              style="margin: 2px 2px"
              icon="pi pi-search"
              v-tooltip.top="'Nachricht öffnen'"
              class="p-button-rounded p-button-warn"
              @click.stop="openConversation(slotProps.data)"
            />
            <Button
              v-if="direction != 'outgoing' && slotProps.data.readTimestamp"
              style="margin: 2px 2px"
              icon="pi pi-envelope"
              v-tooltip.top="'Als ungelesen markieren'"
              class="p-button-rounded p-button-secondary"
              @click.stop="setReadState(slotProps.data, false)"
            />
            <Button
              v-else-if="direction != 'outgoing'"
              style="margin: 2px 2px"
              icon="pi pi-check"
              v-tooltip.top="'Als gelesen markieren'"
              class="p-button-rounded p-button-success"
              @click.stop="setReadState(slotProps.data, true)"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog
      id="messageThreadDialog"
      modal
      v-model:visible="displayThreadDialog"
      style="min-width: 320px; max-width: 720px; width: 90%"
      :closable="true"
    >
      <template #header>
        <h3>Nachricht</h3>
      </template>
      <MessageThread
        v-if="currentConversation"
        :conversation="currentConversation"
        @replied="closeThreadDialogAndReload()"
        @answered="closeThreadDialogAndReload()"
      />
      <template #footer>
        <Button
          v-if="
            currentConversation &&
            !currentConversation.outgoing &&
            currentConversation.readTimestamp
          "
          label="Als ungelesen markieren"
          icon="pi pi-envelope"
          class="p-button-text"
          @click="setReadStateAndClose(false)"
        />
        <Button label="Schließen" icon="pi pi-times" @click="displayThreadDialog = false" />
      </template>
    </Dialog>

    <MessageComposeDialog
      :visible="displayComposeDialog"
      @sent="messageSent"
      @closed="displayComposeDialog = false"
    />
  </div>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import { reportFailure } from "../../services/ServerConnection/FailureToast";
import { useMenuService } from "../../services/MenuService/UseMenuService";
import MenuServiceEventBus from "../../services/MenuService/MenuServiceEventBus";
import { sanitizeHtml } from "../../services/Sanitizer/HtmlSanitizer";
import MessageComposeDialog from "./MessageComposeDialog.vue";
import MessageThread from "./MessageThread.vue";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import SelectButton from "primevue/selectbutton";

export default {
  name: "MessageInbox",
  components: {
    Button,
    Column,
    DataTable,
    Dialog,
    MessageComposeDialog,
    MessageThread,
    SelectButton,
  },
  props: {
    conversationId: [String, Number],
  },
  data() {
    return {
      conversations: [],
      leagueConversations: [],
      direction: "incoming",
      currentConversation: null,
      displayThreadDialog: false,
      displayComposeDialog: false,
      conversationToOpenId: null,
    };
  },
  created() {
    if (!this.loggedIn) {
      localStorage.setItem("routeAfterLogin", this.$route.fullPath);
      this.$router.push("/login");
      return;
    }
    if (this.conversationId) {
      this.conversationToOpenId = this.conversationId;
    }
    if (this.$route.query.neu) {
      this.displayComposeDialog = true;
    }
    this.loadConversations();
    this.loadLeagueConversations();
  },
  mounted() {
    const menuService = useMenuService();
    menuService.setSidebarVisisble(false);
    menuService.setMainMenuItemNames([
      {
        name: "return",
        command: () => {
          if (window.history.length > 1) {
            this.$router.go(-1);
          } else {
            this.$router.push("/");
          }
        },
      },
    ]);
  },
  computed: {
    loggedIn() {
      var user = localStorage.getItem("user");
      return user && user != null;
    },
    availableDirections() {
      var directions = [
        { label: "Empfangen", value: "incoming" },
        { label: "Gesendet", value: "outgoing" },
      ];
      if (this.leagueConversations.length > 0) {
        directions.push({ label: "Meine Ligen", value: "leagues" });
      }
      return directions;
    },
    visibleConversations() {
      return this.conversations.filter((conversation) =>
        this.direction == "outgoing"
          ? conversation.ownMessage
          : conversation.counterpartMessage
      );
    },
    unreadCount() {
      if (this.direction != "incoming") {
        return 0;
      }
      return this.visibleConversations.filter((conversation) => !conversation.readTimestamp)
        .length;
    },
  },
  methods: {
    loadConversations() {
      ServerConnection.get("messages/inbox?offSet=0&limit=500")
        .then((response) => {
          this.conversations = response.data;
          this.publishUnreadCount();
          this.openConversationFromDeepLink();
        })
        .catch((error) => reportFailure(this, error, "Nachrichten konnten nicht geladen werden"));
    },
    loadLeagueConversations() {
      ServerConnection.get("messages/leagues")
        .then((response) => {
          this.leagueConversations = response.data;
        })
        .catch((error) => reportFailure(this, error, "Anfragen deiner Ligen konnten nicht geladen werden"));
    },
    sanitize(html) {
      return sanitizeHtml(html);
    },
    openConversationFromDeepLink() {
      if (!this.conversationToOpenId) {
        return;
      }
      var conversation = this.conversations.find(
        (candidate) => candidate.id == this.conversationToOpenId
      );
      this.conversationToOpenId = null;
      if (conversation) {
        this.openConversation(conversation);
      }
    },
    openConversation(conversation) {
      this.currentConversation = conversation;
      this.displayThreadDialog = true;
      if (!conversation.readTimestamp) {
        this.setReadState(conversation, true);
      }
    },
    reloadCurrentConversation() {
      if (!this.currentConversation) {
        return;
      }
      var conversationId = this.currentConversation.id;
      ServerConnection.get("messages/" + conversationId)
        .then((response) => {
          this.currentConversation = response.data;
          this.replaceConversation(response.data);
        })
        .catch((error) => reportFailure(this, error, "Nachricht konnte nicht geladen werden"));
    },
    replaceConversation(conversation) {
      var index = this.conversations.findIndex(
        (candidate) => candidate.id == conversation.id
      );
      if (index >= 0) {
        this.conversations.splice(index, 1, conversation);
      }
    },
    setReadState(conversation, read) {
      ServerConnection.put("messages/" + conversation.id + (read ? "/read" : "/unread"))
        .then(() => {
          conversation.readTimestamp = read ? this.currentTimestamp() : null;
          this.publishUnreadCount();
          if (read) {
            this.reloadCurrentConversationIfOpen(conversation);
          }
        })
        .catch((error) =>
          reportFailure(this, error, "Lesestatus konnte nicht geändert werden")
        );
    },
    reloadCurrentConversationIfOpen(conversation) {
      if (this.currentConversation && this.currentConversation.id == conversation.id) {
        this.reloadCurrentConversation();
      }
    },
    setReadStateAndClose(read) {
      if (this.currentConversation) {
        this.setReadState(this.currentConversation, read);
      }
      this.displayThreadDialog = false;
    },
    closeThreadDialogAndReload() {
      this.displayThreadDialog = false;
      this.loadConversations();
    },
    messageSent(createdConversations) {
      this.displayComposeDialog = false;
      this.direction = "outgoing";
      this.loadConversations();
      this.$toast.add({
        severity: "success",
        summary: "Nachricht gesendet",
        detail:
          createdConversations > 1
            ? "An " + createdConversations + " Empfänger gesendet."
            : "Die Nachricht wurde gesendet.",
        life: 4000,
      });
    },
    broadcastSize(conversation) {
      if (!conversation.broadcastId) {
        return 1;
      }
      return this.conversations.filter(
        (candidate) => candidate.broadcastId == conversation.broadcastId
      ).length;
    },
    readInfo(conversation) {
      var timestamp =
        this.direction == "outgoing"
          ? conversation.counterpartReadTimestamp
          : conversation.readTimestamp;
      return timestamp ? this.formatDateTime(timestamp) : "ungelesen";
    },
    replyState(conversation) {
      if (conversation.lastMessageOwn == null) {
        return "";
      }
      return conversation.lastMessageOwn ? "beantwortet" : "nicht beantwortet";
    },
    replyStateClass(conversation) {
      return conversation.lastMessageOwn === false ? "unanswered" : null;
    },
    conversationRowClass(conversation) {
      var classes = ["clickable-conversation"];
      if (this.direction == "incoming" && !conversation.readTimestamp) {
        classes.push("unread-conversation");
      }
      return classes;
    },
    routeToLogin() {
      localStorage.setItem("routeAfterLogin", this.$route.fullPath);
      this.$router.push("/login");
    },
    publishUnreadCount() {
      var unread = this.conversations.filter(
        (conversation) => !conversation.readTimestamp
      ).length;
      MenuServiceEventBus.emit("unreadMessages", unread);
    },
    currentTimestamp() {
      var now = new Date();
      var pad = (value) => String(value).padStart(2, "0");
      return (
        now.getFullYear() +
        "-" +
        pad(now.getMonth() + 1) +
        "-" +
        pad(now.getDate()) +
        " " +
        pad(now.getHours()) +
        ":" +
        pad(now.getMinutes())
      );
    },
    formatDateTime(date) {
      if (date && date.length > 0) {
        var pattern = /(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/;
        return date.replace(pattern, "$3.$2.$1 $4:$5 Uhr");
      }
      return "";
    },
  },
};
</script>

<style>
.message-inbox {
  padding: 10px;
}
.message-inbox .inbox-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}
.message-inbox .inbox-count {
  font-size: 13px;
  color: #555;
}
.message-inbox .broadcast-hint {
  color: #888;
  font-size: 12px;
}
.message-inbox .unread-conversation {
  font-weight: 600;
}
.message-inbox .clickable-conversation {
  cursor: pointer;
}
.message-inbox .reply-state {
  font-size: 12px;
  font-weight: 400;
  color: #999;
}
.message-inbox .reply-state.unanswered {
  color: #666;
}
</style>
